/**
 * 月次経理（と決算タスクFMT）の進捗の画面。
 *
 * FMT（型）と同じ並び・同じ色で、選んだ月に起こしたタスクの進み具合を出す。FMT は
 * 「毎月やること」の一覧、この画面は「その月にどこまで終わったか」を見る場所で、
 * 見た目を揃えることで同じ作業の別の面として読めるようにする。
 *
 * 出すのは月次経理として起こしたタスクだけ（種別が月次経理）。手で足した会議などは
 * 混ぜない。締めの進み具合を見る画面なので、混ざると分母が変わってしまう。
 *
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 月次経理の進捗。選んだ月のタスクを大分類・事業・担当・状況・日付のどれかでまとめて出し、状況を直せる
 * @graph-connects groupByCategory [calls] 大分類ごとにまとめる
 * @graph-connects phaseColor [calls] 大分類の色を引く
 * @graph-connects MONTHLY_FMT [calls] 既定は月次経理として起こしたタスクだけに絞る（決算タスクFMT も同じ画面で扱う）
 */
import { useState } from 'react';
import { useSettings } from '@/api/use-settings';
import { StatusBadge } from '@/components/StatusBadge';
import { TaskDetail } from '@/components/TaskDetail';
import { ProgressTitleCell } from '@/components/ProgressTitleCell';
import { listTitleOptionsByKind } from '@/tasks/task-title';
import { fmtManualOf, useFmtItemsByKind } from '@/api/use-monthly-fmt';
import { MONTHLY_FMT, type FmtVariant } from '@/fmt/fmt-variant';
import { formatYearMonth } from '@/tasks/calendar-range';
import { STATUSES, type Task, type TaskStatus } from '@/tasks/task';
import { ALL, phaseColor } from './MonthlyAccounting.js';

/** 対象月に選べる年。FMT と同じ並びにする */
/** @graph-connects none */
const YEARS = ['2025', '2026', '2027', '2028'];

/** 1〜12 月 */
/** @graph-connects none */
const MONTHS = Array.from({ length: 12 }, (_, index) => String(index + 1).padStart(2, '0'));

/** @graph-connects none */
const SELECT_CLASS =
  'rounded border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] px-2.5 py-1.5 text-sm text-[var(--color-text-secondary)]';

/** 進み具合の数え方 */
export interface Progress {
  /** 対象の件数 */
  total: number;
  /** 対応済の件数 */
  done: number;
  /** 対応中の件数 */
  doing: number;
  /** 想定時間の合計 */
  hours: number;
  /** 対応済の想定時間 */
  doneHours: number;
}

/**
 * 進捗として出す 1 行。タスクから、表に出す値だけを取り出したもの。
 */
export interface ProgressRow {
  /** タスクの ID */
  id: string;
  /** 大分類（`00.前準備` など） */
  category: string;
  /** 事業（`共通` `aC` など）。分類の付いていないタスクは空 */
  business: string;
  /** 内容（大分類・事業・中分類を除いた部分） */
  title: string;
  /** 対応日 */
  dueDate: string;
  /** 担当者 */
  assignee: string;
  /** 状況 */
  status: TaskStatus;
  /** 想定時間 */
  estimatedHours: number | null;
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business FMT（月次経理・決算）として起こしたタスクだけを、何月分かで絞って返す。締めるのは翌月なので、対応日ではなく何月分かで見る
 * @graph-connects none
 */
export function monthlyTasks(
  tasks: readonly Task[],
  yearMonth: string,
  // どの FMT のタスクを見るか。決算タスクFMT なら種別「決算」のタスクに絞る
  kind: string = MONTHLY_FMT.kind,
): Task[] {
  return tasks.filter(
    (task) =>
      task.kind === kind &&
      // 何月分かを持たないのは、この項目を足す前に作ったタスク。対応日の月で拾う
      (task.targetMonth ?? task.dueDate.slice(0, 7)) === yearMonth,
  );
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business タスクの内容を、FMT の表と同じ読み方に開く。内容は `大分類_事業_中分類_内容` で作ってあるので、先頭を大分類として切り出す
 * @graph-connects none
 */
export function toProgressRow(task: Task): ProgressRow {
  const [category, ...rest] = task.title.split('_');
  return {
    id: task.id,
    // 分類が付いていないタスク（手で作ったもの）は、まとめて末尾へ寄せる
    category: rest.length === 0 ? '分類なし' : (category ?? '分類なし'),
    // 内容は `大分類_事業_中分類_内容` なので、大分類の次が事業。内容だけのタスクには事業が無い
    business: rest.length < 2 ? '' : (rest[0] ?? ''),
    title: rest.length === 0 ? task.title : rest.join('_'),
    dueDate: task.dueDate,
    assignee: task.assignee,
    status: task.status,
    estimatedHours: task.estimatedHours ?? null,
  };
}

/** 何でまとめるか。`none` はまとめずに 1 つの表で出す */
export type GroupKey = 'category' | 'business' | 'assignee' | 'status' | 'dueDate' | 'none';

/** まとめ方の選択肢。画面の並びもこの順 */
/** @graph-connects none */
export const GROUP_OPTIONS: readonly { key: GroupKey; label: string }[] = [
  { key: 'category', label: '大分類' },
  { key: 'business', label: '事業' },
  { key: 'assignee', label: '担当' },
  { key: 'status', label: '状況' },
  { key: 'dueDate', label: '日付' },
  { key: 'none', label: 'なし' },
];

/** 値の無い行をまとめる見出し。どのまとめ方でも最後に置く */
/** @graph-connects none */
const UNSET: Record<GroupKey, string> = {
  category: '分類なし',
  business: '事業なし',
  assignee: '担当なし',
  status: '状況なし',
  dueDate: '日付なし',
  none: '',
};

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 行がどの見出しに入るかを返す。値が空なら「〜なし」に寄せる
 * @graph-connects none
 */
function groupLabel(row: ProgressRow, by: GroupKey): string {
  const value = by === 'none' ? '' : row[by];
  return value === '' ? UNSET[by] : value;
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 進捗の行を、選んだまとめ方（既定は大分類）で見出しごとにまとめて並べる。大分類は番号順、状況は未着手→対応済の順、日付は早い順、それ以外は名前順で、「〜なし」は最後に置く
 * @graph-connects groupLabel [calls] 行の見出しを決める
 */
export function groupRows(
  rows: readonly ProgressRow[],
  by: GroupKey = 'category',
): { category: string; rows: ProgressRow[] }[] {
  const byLabel = new Map<string, ProgressRow[]>();
  for (const row of rows) {
    const label = groupLabel(row, by);
    const same = byLabel.get(label);
    if (same === undefined) {
      byLabel.set(label, [row]);
    } else {
      same.push(row);
    }
  }
  const unset = UNSET[by];
  return [...byLabel.entries()]
    .map(([category, grouped]) => ({ category, rows: grouped }))
    .sort((a, b) => {
      if (a.category === unset) {
        return 1;
      }
      if (b.category === unset) {
        return -1;
      }
      if (by === 'status') {
        return (
          STATUSES.indexOf(a.category as TaskStatus) - STATUSES.indexOf(b.category as TaskStatus)
        );
      }
      // 大分類は `00.` `01.` … と 2 桁で始まり、日付は `YYYY-MM-DD` なので、文字列の並びがそのまま番号順・日付順になる
      return a.category.localeCompare(b.category, 'ja');
    });
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 進み具合を数える。件数と想定時間の両方を出す
 * @graph-connects none
 */
export function countProgress(rows: readonly ProgressRow[]): Progress {
  return {
    total: rows.length,
    done: rows.filter((row) => row.status === '対応済').length,
    doing: rows.filter((row) => row.status === '対応中').length,
    hours: rows.reduce((sum, row) => sum + (row.estimatedHours ?? 0), 0),
    doneHours: rows
      .filter((row) => row.status === '対応済')
      .reduce((sum, row) => sum + (row.estimatedHours ?? 0), 0),
  };
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 完了率を百分率で返す。1 件も無いときは 0
 * @graph-connects none
 */
export function donePercent(progress: Progress): number {
  return progress.total === 0 ? 0 : Math.round((progress.done / progress.total) * 100);
}

/**
 * MonthlyProgress の props。
 */
export interface MonthlyProgressProps {
  /** いま読み込んでいるタスク */
  tasks: readonly Task[];
  /** 状況を直したときに呼ばれる */
  onUpdateTask: (task: Task) => Promise<boolean>;
  /** タスクを消す。渡さなければ詳細に削除を出さない */
  onDeleteTask?: (id: string) => Promise<boolean>;
  /** 最初に見せる月（`YYYY-MM`） */
  initialMonth?: string;
  /** どの FMT の進捗を見るか。既定は月次経理 */
  variant?: FmtVariant;
}

/**
 * @graph-node {Page}
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business FMT（月次経理・決算）の進捗。月を選ぶと、その月に起こしたタスクの進み具合を FMT と同じ並びで出す
 * @graph-connects monthlyTasks [calls] その FMT のタスクに絞る
 * @graph-connects TaskDetail [calls] 内容を押したタスクの詳細を開く
 * @graph-connects ProgressTitleCell [calls] 行の区分を丸いプルダウンで出す
 * @graph-connects listTitleOptionsByKind [calls] 種別ごとの区分の選択肢を集める
 * @graph-connects useFmtItemsByKind [calls] 区分の選択肢の元になる FMT を読む
 * @graph-connects fmtManualOf [calls] 元の FMT 項目のマニュアルを引く
 * @graph-connects countProgress [calls] 進み具合を数える
 */
export function MonthlyProgress({
  tasks,
  onUpdateTask,
  onDeleteTask,
  initialMonth = '2026-09',
  variant = MONTHLY_FMT,
}: MonthlyProgressProps) {
  const { settings: kindSettings } = useSettings('kind-settings');
  const [month, setMonth] = useState(initialMonth);
  const [assignee, setAssignee] = useState(ALL);
  const [statusFilter, setStatusFilter] = useState(ALL);
  const [business, setBusiness] = useState(ALL);
  const [groupBy, setGroupBy] = useState<GroupKey>('category');
  const [query, setQuery] = useState('');

  const monthly = monthlyTasks(tasks, month, variant.kind);
  const keyword = query.trim();
  const rows = monthly
    .map(toProgressRow)
    .filter(
      (row) =>
        (assignee === ALL || row.assignee === assignee) &&
        (statusFilter === ALL || row.status === statusFilter) &&
        (business === ALL || row.business === business) &&
        (keyword === '' || row.title.includes(keyword)),
    );
  const groups = groupRows(rows, groupBy);
  // 進み具合は、絞り込みを掛ける前のその月の全体で数える（分母が動くと締めの判断を誤る）
  const progress = countProgress(monthly.map(toProgressRow));
  const percent = donePercent(progress);
  const assignees = [...new Set(monthly.map((task) => task.assignee))]
    .filter((name) => name !== '')
    .sort((a, b) => a.localeCompare(b, 'ja'));
  const businesses = [...new Set(monthly.map((task) => toProgressRow(task).business))]
    .filter((name) => name !== '')
    .sort((a, b) => a.localeCompare(b, 'ja'));

  // 内容を押して開いたタスク。直したあとも最新の中身を出すよう、ID で持って一覧から引く
  const [selectedId, setSelectedId] = useState('');
  // 区分の選択肢の元（月次経理・決算タスクFMT の全項目）
  const fmtItemsByKind = useFmtItemsByKind();
  const titleOptionsByKind = listTitleOptionsByKind(tasks, fmtItemsByKind);
  /** 行のもとのタスク。区分を選び直すときに、内容以外はそのまま保つため */
  const taskOf = (row: ProgressRow): Task =>
    tasks.find((task) => task.id === row.id) ?? {
      id: row.id,
      team: 'sft',
      kind: variant.kind,
      title: `${row.category}_${row.title}`,
      assignee: row.assignee,
      dueDate: row.dueDate,
      status: row.status,
      source: 'fmt',
    };
  const selected = tasks.find((task) => task.id === selectedId);
  // 種別の選択肢には、種別設定に登録したもの（まだタスクが無い種別）も入れる。進捗は SFT のタスクだけを扱う
  const kindOptions = [
    ...new Set([
      ...kindSettings.filter((setting) => setting.team === 'sft').map((setting) => setting.name),
      ...tasks.map((task) => task.kind),
    ]),
  ].sort((a, b) => a.localeCompare(b, 'ja'));
  const assigneeOptions = [...new Set(tasks.map((task) => task.assignee))]
    .filter((name) => name !== '')
    .sort((a, b) => a.localeCompare(b, 'ja'));

  const changeStatus = (row: ProgressRow, status: TaskStatus) => {
    const target = monthly.find((task) => task.id === row.id);
    if (target !== undefined) {
      // 失敗したときの理由は画面上部の帯が出す
      void onUpdateTask({ ...target, status });
    }
  };

  return (
    <main className="min-w-0 flex-1 overflow-y-auto px-8 py-5">
      {/* 見出し・絞り込み・対象月と進み具合は、一覧をスクロールしても上に残す */}
      <div className="sticky -top-5 z-[5] -mx-8 -mt-5 bg-[var(--color-bg-base)] px-8 pt-5 pb-1">
        <header className="mb-4 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="m-0 text-xs font-bold tracking-wide text-[var(--color-text-secondary)]">
              {variant.section}
            </p>
            <h1 className="mt-0.5 mb-0 text-lg font-semibold tracking-tight text-[var(--color-text-primary)]">
              進捗
            </h1>
            <p className="mt-0.5 mb-0 text-xs text-[var(--color-text-secondary)]">
              {variant.periodLabel}で選びます（作業するのは翌月）。状況はこの画面から直せます
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <label htmlFor="progress-group" className="sr-only">
              まとめ方
            </label>
            <select
              id="progress-group"
              value={groupBy}
              onChange={(event) => {
                setGroupBy(event.target.value as GroupKey);
              }}
              className={SELECT_CLASS}
            >
              {GROUP_OPTIONS.map((option) => (
                <option key={option.key} value={option.key}>
                  グループ：{option.label}
                </option>
              ))}
            </select>

            <label htmlFor="progress-business" className="sr-only">
              事業で絞り込む
            </label>
            <select
              id="progress-business"
              value={business}
              onChange={(event) => {
                setBusiness(event.target.value);
              }}
              className={SELECT_CLASS}
            >
              <option value={ALL}>事業：すべて</option>
              {businesses.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>

            <label htmlFor="progress-assignee" className="sr-only">
              担当者で絞り込む
            </label>
            <select
              id="progress-assignee"
              value={assignee}
              onChange={(event) => {
                setAssignee(event.target.value);
              }}
              className={SELECT_CLASS}
            >
              <option value={ALL}>担当：すべて</option>
              {assignees.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>

            <label htmlFor="progress-status" className="sr-only">
              状況で絞り込む
            </label>
            <select
              id="progress-status"
              value={statusFilter}
              onChange={(event) => {
                setStatusFilter(event.target.value);
              }}
              className={SELECT_CLASS}
            >
              <option value={ALL}>状況：すべて</option>
              {STATUSES.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>

            <label htmlFor="progress-query" className="sr-only">
              タスク名で検索
            </label>
            <input
              id="progress-query"
              type="search"
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
              }}
              placeholder="タスク名で検索"
              className={`${SELECT_CLASS} w-44`}
            />
          </div>
        </header>

        {/* 対象月と進み具合。FMT の「タスクを作成する」の帯と同じ位置に置き、
          同じ画面の別の面として読めるようにする */}
        <div className="mb-3 flex flex-wrap items-center gap-3 rounded border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] px-4 py-3">
          <label htmlFor="progress-month" className="text-sm text-[var(--color-text-secondary)]">
            {variant.periodLabel}
          </label>
          <select
            id="progress-month"
            value={month.slice(0, 4)}
            onChange={(event) => {
              setMonth(`${event.target.value}-${month.slice(5, 7)}`);
            }}
            className={SELECT_CLASS}
          >
            {YEARS.map((year) => (
              <option key={year} value={year}>
                {year}年
              </option>
            ))}
          </select>
          <label htmlFor="progress-month-number" className="sr-only">
            {variant.periodLabel}の月
          </label>
          <select
            id="progress-month-number"
            value={month.slice(5, 7)}
            onChange={(event) => {
              setMonth(`${month.slice(0, 4)}-${event.target.value}`);
            }}
            className={SELECT_CLASS}
          >
            {MONTHS.map((value) => (
              <option key={value} value={value}>
                {Number(value)}月
              </option>
            ))}
          </select>

          {progress.total === 0 ? (
            <span className="text-xs text-[var(--color-text-muted)]">
              {formatYearMonth(month)}
              {variant.periodSuffix}のタスクはまだ作成されていません（FMT
              の「タスクを作成する」から作れます）
            </span>
          ) : (
            <>
              {/* 残りが何件かが主役。棒は見出しではなく、数字の裏づけとして添える */}
              <span className="text-sm text-[var(--color-text-primary)]">
                残り{' '}
                <span className="font-semibold tabular-nums">{progress.total - progress.done}</span>{' '}
                件
              </span>
              <span className="min-w-40 flex-1">
                <span
                  aria-hidden="true"
                  className="block h-1.5 overflow-hidden rounded-full bg-[var(--color-bg-subtle)]"
                >
                  <span
                    className="block h-full rounded-full bg-[var(--color-kind-meeting)]"
                    style={{ width: `${String(percent)}%` }}
                  />
                </span>
              </span>
              <span className="text-xs tabular-nums text-[var(--color-text-secondary)]">
                対応済 {progress.done} / {progress.total}（{percent}%）・対応中 {progress.doing}
              </span>
              <span className="text-xs tabular-nums text-[var(--color-text-muted)]">
                想定 {progress.doneHours.toFixed(2)} / {progress.hours.toFixed(2)} 時間
              </span>
            </>
          )}
        </div>
      </div>

      {rows.length === 0 ? (
        <p className="py-16 text-center text-sm text-[var(--color-text-muted)]">
          {progress.total === 0
            ? `${formatYearMonth(month)}${variant.periodSuffix}の${variant.kind}タスクはありません`
            : '条件に合うタスクはありません'}
        </p>
      ) : (
        <table className="w-full border-collapse text-left text-sm">
          <thead>
            <tr className="border-b border-[var(--color-border-subtle)] bg-[var(--color-bg-subtle)]">
              <th className="py-2 pr-3 font-medium text-[var(--color-text-muted)]">内容</th>
              <th className="w-28 py-2 pr-3 font-medium text-[var(--color-text-muted)]">対応日</th>
              <th className="w-16 py-2 pr-3 text-right font-medium text-[var(--color-text-muted)]">
                想定
              </th>
              <th className="w-24 py-2 pr-3 font-medium text-[var(--color-text-muted)]">担当</th>
              <th className="w-44 py-2 font-medium text-[var(--color-text-muted)]">状況</th>
            </tr>
          </thead>
          {groups.map((group) => {
            const groupProgress = countProgress(group.rows);
            return (
              <tbody key={group.category}>
                {/* 見出しは行をまたぐ。大分類なら FMT と同じ色にして、同じ括りだと分かるようにする。
                    まとめない（なし）ときは見出しを出さない */}
                {groupBy !== 'none' && (
                  <tr className="border-b border-[var(--color-border-subtle)]">
                    <th
                      colSpan={5}
                      style={{
                        backgroundColor: phaseColor(group.category).background,
                        borderLeft: `3px solid ${phaseColor(group.category).color}`,
                      }}
                      className="py-1.5 pl-2 text-left text-sm font-bold text-[var(--color-text-primary)]"
                    >
                      {group.category}
                      <span className="ml-2 text-xs font-normal tabular-nums text-[var(--color-text-secondary)]">
                        対応済 {groupProgress.done} / {groupProgress.total}
                      </span>
                    </th>
                  </tr>
                )}
                {group.rows.map((row) => (
                  <tr
                    key={row.id}
                    className={
                      row.status === '対応済'
                        ? // 終わった行は沈める。残っているものを探すために見る画面なので
                          'border-b border-[var(--color-border-subtle)] text-[var(--color-text-muted)] opacity-45'
                        : 'border-b border-[var(--color-border-subtle)]'
                    }
                  >
                    <td className="py-2 pr-3 align-top text-[var(--color-text-primary)]">
                      {/* 区分は詳細と同じ丸いプルダウンで出して、その場で選び直せる。
                          作業の名前を押すと、タスク管理と同じ詳細を開く */}
                      <ProgressTitleCell
                        task={taskOf(row)}
                        options={titleOptionsByKind[variant.kind]}
                        showCategory={groupBy !== 'category'}
                        onChange={(changed) => {
                          // 失敗したときの理由は画面上部の帯が出す
                          void onUpdateTask(changed);
                        }}
                        onOpen={() => {
                          setSelectedId(row.id);
                        }}
                      />
                    </td>
                    <td className="py-2 pr-3 align-top whitespace-nowrap tabular-nums text-[var(--color-text-secondary)]">
                      {row.dueDate}
                    </td>
                    <td className="py-2 pr-3 align-top text-right tabular-nums text-[var(--color-text-secondary)]">
                      {row.estimatedHours === null ? '—' : row.estimatedHours.toFixed(2)}
                    </td>
                    <td className="py-2 pr-3 align-top text-[var(--color-text-secondary)]">
                      {row.assignee}
                    </td>
                    <td className="flex items-center gap-2 py-2 align-top">
                      <StatusBadge status={row.status} />
                      <label htmlFor={`status-${row.id}`} className="sr-only">
                        {row.title}の状況
                      </label>
                      <select
                        id={`status-${row.id}`}
                        value={row.status}
                        onChange={(event) => {
                          changeStatus(row, event.target.value as TaskStatus);
                        }}
                        className="cursor-pointer rounded border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] px-1.5 py-0.5 text-xs text-[var(--color-text-secondary)]"
                      >
                        {STATUSES.map((value) => (
                          <option key={value} value={value}>
                            {value}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            );
          })}
        </table>
      )}

      {selected === undefined ? null : (
        <TaskDetail
          task={selected}
          fmtManual={fmtManualOf(selected, fmtItemsByKind)}
          onDelete={
            onDeleteTask === undefined
              ? undefined
              : () => {
                  void onDeleteTask(selected.id);
                  setSelectedId('');
                }
          }
          assignees={assigneeOptions}
          kinds={kindOptions}
          titleOptionsByKind={titleOptionsByKind}
          onSave={(saved) => {
            // 失敗したときの理由は画面上部の帯が出す
            void onUpdateTask(saved);
            setSelectedId('');
          }}
          onClose={() => {
            setSelectedId('');
          }}
        />
      )}
    </main>
  );
}
